import type { ProwlEvent, ProwlRequest } from './contracts';
import {
  getPersistedSessionId,
  getPersistedActiveBusinessId,
} from '../../utils/sessionHeaderStorage';

/**
 * hp-ai-service is now reachable through the same API Gateway as every other
 * backend call, mounted at /aiservice (verified via
 * https://api-uat.hirepanther.ca/aiservice/health). VITE_APP_BACKEND_PROWL
 * overrides the mount path when needed; it is combined with
 * VITE_APP_BACKEND_PORT the same way src/api/index.ts builds its axios
 * baseURL, rather than being a CloudFront-relative path like
 * VITE_APP_BACKEND_CHAT / VITE_APP_BACKEND_TRUST_SAFETY.
 */
const BACKEND_PORT = import.meta.env.VITE_APP_BACKEND_PORT ?? '';
const PROWL_MOUNT = import.meta.env.VITE_APP_BACKEND_PROWL || '/aiservice';
const PROWL_BASE_URL = `${BACKEND_PORT}${PROWL_MOUNT}`;
const PROWL_STREAM_PATH = '/api/v2/prowl/stream';

const SSE_FRAME_SEPARATOR = '\n\n';

interface ParsedFrame {
  event: string | null;
  data: string;
}

const parseFrame = (raw: string): ParsedFrame => {
  let event: string | null = null;
  const dataLines: string[] = [];

  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  return { event, data: dataLines.join('\n') };
};

export interface StreamProwlOptions {
  signal?: AbortSignal;
  onEvent: (evt: ProwlEvent) => void;
  /** Called once with the raw HTTP status before any SSE bytes are read. */
  onHttpError?: (status: number) => void;
}

/**
 * Opens a POST-based SSE connection to hp-ai-service and dispatches each
 * frame as a typed ProwlEvent. `EventSource` cannot be used here — it only
 * supports GET and cannot carry a JSON body (design-spec §7's request is a
 * POST body, not query params).
 *
 * Auth: same as every other call this app makes — the httpOnly session_id
 * cookie, sent via `credentials: 'include'` (see src/api/index.ts's
 * `withCredentials` axios instance). hp-ai-service no longer accepts an
 * `Authorization: Bearer` header, so none is sent. The same request-context
 * headers the axios client attaches on every call (src/api/index.ts:71-102)
 * are attached here too, since hp-ai-service's auth middleware relies on
 * them the same way. `onHttpError` / an `error` ProwlEvent are how a
 * rejected request surfaces instead of hanging silently.
 */
export const streamProwl = async (
  request: ProwlRequest,
  { signal, onEvent, onHttpError }: StreamProwlOptions
): Promise<void> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    'x-client-portal': 'customer',
    'x-active-role': 'customer',
  };
  const sessionId = getPersistedSessionId();
  if (sessionId) headers['x-session-id'] = sessionId;
  const activeBusinessId = getPersistedActiveBusinessId();
  if (activeBusinessId) headers['x-active-business-id'] = activeBusinessId;

  const response = await fetch(`${PROWL_BASE_URL}${PROWL_STREAM_PATH}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok || !response.body) {
    onHttpError?.(response.status);
    onEvent({
      event: 'error',
      data: {
        code:
          response.status === 401 || response.status === 403
            ? 'unauthorized'
            : 'http_error',
        message: 'Prowl is temporarily unavailable.',
        retryable: response.status >= 500,
      },
    });
    onEvent({ event: 'done', data: { turnId: '', truncated: false } });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let sepIndex = buffer.indexOf(SSE_FRAME_SEPARATOR);
      while (sepIndex !== -1) {
        const rawFrame = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + SSE_FRAME_SEPARATOR.length);

        const { event, data } = parseFrame(rawFrame);
        if (event && data) {
          try {
            onEvent({ event, data: JSON.parse(data) } as ProwlEvent);
          } catch {
            // Malformed frame — drop it rather than crash the stream.
          }
        }

        sepIndex = buffer.indexOf(SSE_FRAME_SEPARATOR);
      }
    }
  } finally {
    reader.releaseLock();
  }
};
